// Meeting types
/**
 * @deprecated The backend has replaced `meeting` with `source_info`.
 *             Use `SourceInfoDto` instead.
 */
export interface MeetingInfoDto {
    created_at: string
    title: string
    attendees: string[]
    transcript: string | null
}

// Generic source information covering meetings or direct conversations
interface BaseSourceInfoDto {
    /** Discriminator for the source type */
    type: 'meeting' | 'direct_conversation'
    created_at: string
    title: string
    /** Formatted transcript text – optional */
    transcript?: string | null
}

export interface MeetingSourceInfoDto extends BaseSourceInfoDto {
    type: 'meeting'
    attendees?: string[]
}

export interface DirectConversationSourceInfoDto extends BaseSourceInfoDto {
    type: 'direct_conversation'
    // Potential future fields (e.g., phone_number) can be added here
}

export type SourceInfoDto = MeetingSourceInfoDto | DirectConversationSourceInfoDto

// Admin user types
export interface AdminUserDto {
    id: string
    name: string
    email: string
    username: string
    slug: string
    pending_transcript_chapter_count?: number
    last_emailed_at?: string | null
    num_draft_posts_since_last_email?: number
}

export interface ListUsersQueryDto {
    search?: string
    page?: number
    limit?: number
}

// Draft post types
export interface AdminDraftPostDto {
    id: string
    status: string
    idea_title: string
    idea_basis: string
    idea_core_idea: string
    idea_relation_to_target_audience: string
    idea_research_enhancement_explanation: string
    post_content?: string
    admin_feedback?: string
    user_feedback?: string | null
    user_feedback_sentiment?: DraftPostFeedbackSentiment | null
    derived_from?: DraftPostDerivation
    published_at?: string | null
    user: {
        id: string
        name: string
        last_emailed_at?: string | null
    }
    created_at: string
    updated_at: string
    /**
     * Information about the primary source of the draft post (meeting, phone call, etc.)
     */
    source_info: SourceInfoDto | null
}

export enum DraftPostDerivation {
    TRANSCRIPT_CHAPTERS_AND_RESEARCH = 'transcript_chapters_and_research',
    ONLY_TRANSCRIPT_CHAPTERS = 'only_transcript_chapters',
}

export interface ResearchResult {
    id: string
    title: string
    summary: string
    url: string
    domain?: string
    connection_status: ChapterConnectionStatus
}

export interface TranscriptChapter {
    id: string
    chapter_title: string
    summary: string
    key_quotes: string[]
    connection_status: ChapterConnectionStatus
    /**
     * Source information for this transcript chapter (meeting, phone call, etc.)
     */
    source_info: SourceInfoDto | null
}

export interface AdminDraftPostDetailedDto extends AdminDraftPostDto {
    research_results: ResearchResult[]
    transcript_chapters: TranscriptChapter[]
    feedback_last_saved?: string // Timestamp when feedback was last saved
}

export interface ListDraftPostsQueryDto {
    user_id?: string
    status?: string
    page?: number
    limit?: number
}

// Feedback types
export interface CreateDraftPostFeedbackDto {
    content: string
}

export interface DraftPostFeedbackResponseDto {
    id: string
    content: string
}

// Status types
export interface UpdateDraftPostStatusDto {
    status: string
}

export interface UpdateDraftPostStatusResponseDto {
    id: string
    status: string
    updated_at: string
}

// Bulk Status Update types
export interface BulkUpdateDraftPostStatusDto {
    ids: string[]
    status: string
}

export interface BulkUpdateDraftPostStatusResponseDto {
    successful_updates: number
    failed_updates: number
    // Optionally, include details about failures if needed later
    // errors: { id: string; error: string }[];
}

// Enums
export enum DraftPostViewMode {
    IDEAS = 'ideas',
    POSTS = 'posts',
    FEEDBACK = 'feedback',
}

export enum DraftPostFeedbackSentiment {
    POSITIVE = 'positive',
    NEGATIVE = 'negative',
}

export enum ChapterConnectionStatus {
    USED = 'connection_found',
    UNUSED = 'no_connection_found',
}

export enum DraftPostStatus {
    PENDING_CREATION = 'pending_creation',
    PENDING_RETRY = 'pending_retry',
    IDEA_UNDER_REVIEW = 'idea_under_review',
    IDEA_APPROVED_WITH_RESEARCH = 'idea_approved_with_research',
    IDEA_APPROVED_WITHOUT_RESEARCH = 'idea_approved_without_research',
    IDEA_REJECTED = 'idea_rejected',
    POST_UNDER_REVIEW = 'post_under_review',
    POST_APPROVED = 'post_approved',
    POST_REJECTED = 'post_rejected',
}

// --- NEW User Management Types ---

// Organization Types
export interface OrgDto {
    id: string
    name: string
}

export interface GetOrgsResponseDto {
    orgs: OrgDto[]
}

export interface CreateOrgDto {
    name: string
}

// User Relevant Topics and Target Audience Types
export interface TargetAudienceEntryDto {
    audience_name: string
    description: string
}

export interface UserRelevantTopicsDto {
    target_audience_structured?: TargetAudienceEntryDto[]
    tone_of_voice?: string
    bio?: string
}

// User Creation and Update Types
// NOTE: Corresponds to backend CreateUserDto
export interface AdminCreateUserDto {
    firstName: string
    lastName: string
    title?: string
    email: string
    userOrgName: string // User's company/org name (text)
    orgId: string // ID of the licensing OrgEntity
    defaultTimezone: string // Assuming UserTimeZones enum exists elsewhere or using string
    linkedinUrl?: string // Optional LinkedIn profile URL
    // insights_enabled, account_claimed, invite_code, confirmed_at, shared_profiles are handled backend
}

// NOTE: Corresponds to backend UpdateUserDto
export interface AdminUpdateUserDto {
    firstName?: string
    lastName?: string
    title?: string
    email?: string
    userOrgName?: string
    alternate_names?: string
    default_timezone?: string // Assuming UserTimeZones enum exists elsewhere or using string
    relevantTopics?: UserRelevantTopicsDto // Nested update
}

// User Retrieval Type
// NOTE: Corresponds to backend GetUserResponseDto
export interface AdminGetUserResponseDto {
    id: string
    first_name: string
    last_name: string
    title?: string
    email: string
    username: string
    user_org_name: string
    default_timezone: string
    alternate_names?: string
    userRelevantTopics?: UserRelevantTopicsDto
    org?: OrgDto
    slug?: string
    pending_transcript_chapter_count?: number
    last_emailed_at?: string | null
    num_draft_posts_since_last_email?: number
}

// LinkedIn Scrape Type
// NOTE: Corresponds to backend TriggerLinkedInScrapeDto
export interface AdminTriggerLinkedInScrapeDto {
    linkedinUrl: string
}

// ---  Research Result Types ---

export interface GetResearchResultsQueryDto {
    page?: number
    limit?: number
}

export interface GetResearchResultsParamsDto {
    userId: string
}

export interface ResearchResultItemDto {
    relevance_reason: string
    published_at: Date
    created_at: Date
    title: string
    summary: string
    url: string
    id: string
}

export interface PaginatedResearchResultsDto {
    data: ResearchResultItemDto[]
    total: number
    page: number
    limit: number
    totalPages: number
    hasNextPage: boolean
    hasPrevPage: boolean
}
